import type {
  AccountsData,
  ProfileItem,
  ProfilesData,
  User,
  UserAccountDetails,
} from "../../store/useAuthStore";
import type {
  AccountsOfLoggedInUserItem,
  InfinityLearnCookiePayload,
  InfinityLearnUserDto,
  MappedHandoffAuth,
  ProfilesOfLoggedInUserPayload,
} from "./types";
import {
  normalizeProfilesOrder,
  pickDefaultProfile,
} from "./normalizeProfiles";

const HANDOFF_LOG_PREFIX = "[IL Handoff]";

const PROFILE_ONLY_KEYS = new Set([
  "uam_id",
  "admission_id",
  "crn_id",
  "branch_id",
  "tenant_id",
  "vertical_id",
  "is_default_profile",
  "is_temp_password",
  "tenant_code",
  "exams",
  "studentExams",
  "studentGrade",
  "firstName",
  "lastName",
  "isdCode",
  "userId",
  "lsq_exams",
  "unified_account_id",
]);

function buildUserFromAccountDetails(
  unified_account_id: string,
  user_account_details: UserAccountDetails,
): User {
  const displayName =
    `${user_account_details.first_name ?? ""} ${user_account_details.last_name ?? ""}`.trim() ||
    "User";
  return {
    id: unified_account_id,
    name: displayName,
    role: "student",
  };
}

function buildProfileItemFromUserDto(
  userDto: InfinityLearnUserDto,
): ProfileItem {
  return {
    uam_id: userDto.uam_id,
    admission_id: userDto.admission_id,
    crn_id: userDto.crn_id,
    branch_id: userDto.branch_id,
    tenant_id: userDto.tenant_id,
    vertical_id: userDto.vertical_id,
    is_default_profile: userDto.is_default_profile ?? true,
    is_temp_password: userDto.is_temp_password,
    tenant_code: userDto.tenant_code,
    exams: userDto.exams ?? userDto.studentExams,
  };
}

function buildUserAccountDetailsFromUserDto(
  userDto: InfinityLearnUserDto,
): UserAccountDetails {
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(userDto)) {
    if (!PROFILE_ONLY_KEYS.has(key)) {
      rest[key] = value;
    }
  }

  return {
    ...rest,
    first_name: userDto.first_name ?? userDto.firstName,
    last_name: userDto.last_name ?? userDto.lastName,
    isd_code: userDto.isd_code ?? userDto.isdCode,
    phone: userDto.phone,
    email: userDto.email ?? undefined,
    profile_photo: userDto.profile_photo,
    whatsapp_consent: userDto.whatsapp_consent,
    grade: userDto.grade ?? userDto.studentGrade,
    user_type: userDto.user_type,
    role: userDto.role,
  } as UserAccountDetails;
}

function buildProfilesDataFromProfilesCookie(
  accessToken: string,
  profilesCookie: ProfilesOfLoggedInUserPayload,
): { profilesData: ProfilesData; defaultProfile: ProfileItem } {
  const profiles = normalizeProfilesOrder(profilesCookie.profiles);
  const defaultProfile = pickDefaultProfile(profiles);
  if (!defaultProfile) {
    throw new Error("profilesOfLoggedInUser cookie has no valid profiles.");
  }

  const profilesData: ProfilesData = {
    access_token: accessToken,
    unified_account_details: {
      unified_account_id: profilesCookie.unified_account_id,
      user_account_details: profilesCookie.user_account_details,
      profiles,
    },
  };

  return { profilesData, defaultProfile };
}

function buildProfilesDataFromUserDto(
  accessToken: string,
  userDto: InfinityLearnUserDto,
): { profilesData: ProfilesData; defaultProfile: ProfileItem } {
  const unified_account_id = String(userDto.unified_account_id);
  const defaultProfile = buildProfileItemFromUserDto(userDto);
  const user_account_details = buildUserAccountDetailsFromUserDto(userDto);

  const profilesData: ProfilesData = {
    access_token: accessToken,
    unified_account_details: {
      unified_account_id,
      user_account_details,
      profiles: [defaultProfile],
    },
  };

  return { profilesData, defaultProfile };
}

function buildAccountsData(
  cookie: InfinityLearnCookiePayload,
  accountsFromCookie: AccountsOfLoggedInUserItem[] | null,
  unified_account_id: string,
  user_account_details: UserAccountDetails,
): AccountsData {
  const userDto = cookie.userDto;
  const unified_accounts = accountsFromCookie?.map((account) => ({
    unified_account_id: account.unified_account_id,
    first_name: account.first_name,
    last_name: account.last_name,
    grade: account.grade,
    user_account_photo: account.user_account_photo,
  })) ?? [
    {
      unified_account_id,
      first_name: String(
        user_account_details.first_name ?? userDto.first_name ?? "",
      ),
      last_name: String(
        user_account_details.last_name ?? userDto.last_name ?? "",
      ),
      grade:
        user_account_details.grade?.gradeId ??
        userDto.grade?.gradeId ??
        userDto.studentGrade?.gradeId,
      user_account_photo: user_account_details.profile_photo ?? null,
    },
  ];

  return {
    access_token: cookie.login_token ?? cookie.accessToken,
    refresh_token: "",
    is_user_existing: true,
    unified_accounts,
  };
}

/**
 * Maps IL portal cookies into shapes compatible with {@link useAuthStore}.
 * Prefer `profilesOfLoggedInUser` for profile shape; `userDto` is fallback for tokens + profile.
 */
export function mapCookiesToAuthState(
  cookie: InfinityLearnCookiePayload,
  accountsFromCookie: AccountsOfLoggedInUserItem[] | null,
  profilesFromCookie: ProfilesOfLoggedInUserPayload | null,
): MappedHandoffAuth {
  const accessToken = cookie.accessToken.trim();
  const userDto = cookie.userDto;

  if (
    profilesFromCookie &&
    profilesFromCookie.unified_account_id !== userDto.unified_account_id
  ) {
    console.warn(
      `${HANDOFF_LOG_PREFIX} profilesOfLoggedInUser unified_account_id does not match INFINITY_LEARN.userDto; using profiles cookie.`,
    );
  }

  const { profilesData, defaultProfile } = profilesFromCookie
    ? buildProfilesDataFromProfilesCookie(accessToken, profilesFromCookie)
    : buildProfilesDataFromUserDto(accessToken, userDto);

  const unified_account_id =
    profilesData.unified_account_details?.unified_account_id ??
    String(userDto.unified_account_id);
  const user_account_details =
    profilesData.unified_account_details?.user_account_details ??
    buildUserAccountDetailsFromUserDto(userDto);

  const user = buildUserFromAccountDetails(
    unified_account_id,
    user_account_details,
  );
  const accountsData = buildAccountsData(
    cookie,
    accountsFromCookie,
    unified_account_id,
    user_account_details,
  );

  return {
    accessToken,
    user,
    profilesData,
    accountsData,
    defaultProfile,
  };
}
